import { getOwner } from '@ember/application';
import DS from 'ember-data';
import Route from 'cio/routes/base';
import { inject as service } from '@ember/service';
import RouterService from '@ember/routing/router-service';
import TransactionalMessageModel from 'cio/models/transactional-message';
import ComposerTransactionalTemplateController from 'cio/pods/composer/transactional/template/controller';
import Transition from '@ember/routing/-private/transition';
import TemplateModel from 'cio/models/template';
import { createComposerEditableEntity } from 'cio/utils/composer/composer-editable-entity';
import ConfirmChangesService from 'cio/services/confirm-changes';
import { ComposerSection } from 'cio/components/action-composer';

export default class TransactionalMessageTemplateRoute extends Route {
  @service store!: DS.Store;
  @service router!: RouterService;
  @service confirmChanges!: ConfirmChangesService;

  async model(params: { transactional_message_id: string; template_id: string }) {
    let message = this.modelFor('composer.transactional') as TransactionalMessageModel;
    let template = this.store.peekRecord('template', params.template_id);

    return {
      message,
      template,
    };
  }

  redirect(model: { message: TransactionalMessageModel; template: TemplateModel | null }) {
    // Make sure `template_id` param exists and belongs to the message.
    let templateTransactionalMessageId = model.template?.belongsTo('transactionalMessage').id();
    if (templateTransactionalMessageId !== model.message.id) {
      this.router.transitionTo(
        'composer.transactional.template',
        model.message.id,
        model.message.belongsTo('template').id(),
      );
    }
  }

  setupController(
    controller: ComposerTransactionalTemplateController,
    model: { message: TransactionalMessageModel; template: TemplateModel },
    transition: Transition,
  ) {
    super.setupController(controller, model, transition);

    let owner = getOwner(this);
    let { message, template } = model;
    let editableEntity = createComposerEditableEntity(owner, { message, template });

    controller.setProperties({
      editableEntity,
      selectedEditor: null,
    });

    controller.setEditor();

    // When transitioning from a transactional sub-route, we want the "Back"
    // button to take the user to the route that they came from, so we set the
    // last active route on transition.
    if (
      ['transactional.message.setup', 'transactional.message.overview'].any(
        (routeName) => transition?.from?.name.includes(routeName) ?? false,
      )
    ) {
      controller.set('lastActiveTransactionalRoute', transition.from?.name);
    }

    this.confirmChanges.register(controller, this);
  }

  resetController(
    controller: ComposerTransactionalTemplateController,
    isExiting: boolean,
    transition: Transition,
  ): void {
    super.resetController(controller, isExiting, transition);

    controller.set('currentTab', ComposerSection.Editor);
  }
}
